/**
 * extensionSafeRequest — bypass de extensões agressivas de navegador
 * (Ask Gemini, Honey, etc.) que fazem monkey-patch em `window.fetch`,
 * `window.XMLHttpRequest` e até `Worker.prototype.postMessage` na main thread.
 *
 * Estratégia: roda a request HTTP dentro de um Web Worker construído a partir
 * de um Blob URL. Workers de blob têm escopo global isolado E sua origem é
 * única (blob:...), o que impede que extensões injetem content scripts
 * dentro deles. É a forma mais confiável de fazer uma request sem que
 * extensões interfiram.
 *
 * Uso:
 *   const data = await extensionSafeRequest({
 *     url: '/api/admin/auth/login',
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify({ username, password }),
 *   });
 *
 * Para upload de arquivo:
 *   const data = await extensionSafeRequest({
 *     url: '/api/.../import',
 *     method: 'POST',
 *     headers: { Authorization: 'Bearer ...' },
 *     file: <File>,        // será enviado como multipart/form-data
 *     onProgress: (pct) => {...},
 *   });
 */

// Código do worker como string. Roda dentro do Blob URL Worker (escopo isolado).
const WORKER_SOURCE = `
self.addEventListener('message', (e) => {
  const req = e.data || {};
  try {
    const xhr = new XMLHttpRequest();
    xhr.open(req.method || 'POST', req.url, true);
    xhr.responseType = 'text';

    const headers = req.headers || {};
    Object.keys(headers).forEach((k) => {
      try { xhr.setRequestHeader(k, headers[k]); } catch (_) {}
    });

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        self.postMessage({ type: 'progress', percent: Math.round((ev.loaded / ev.total) * 100) });
      }
    };

    xhr.onload = () => {
      let data = xhr.responseText;
      try { data = JSON.parse(xhr.responseText); } catch (_) {}
      self.postMessage({
        type: 'done',
        status: xhr.status,
        ok: xhr.status >= 200 && xhr.status < 300,
        data: data,
      });
    };

    xhr.onerror = () => {
      self.postMessage({ type: 'error', message: 'Falha de rede' });
    };

    xhr.onabort = () => {
      self.postMessage({ type: 'error', message: 'Cancelado' });
    };

    // Reconstrói o body
    if (req.bodyKind === 'file') {
      const fd = new FormData();
      fd.append('file', req.fileBlob, req.fileName || 'upload.bin');
      xhr.send(fd);
    } else if (req.bodyKind === 'string') {
      xhr.send(req.body || null);
    } else {
      xhr.send(null);
    }
  } catch (err) {
    self.postMessage({ type: 'error', message: (err && err.message) || 'Erro no worker' });
  }
});
`;

let cachedWorkerUrl = null;

function getWorkerUrl() {
  if (cachedWorkerUrl) return cachedWorkerUrl;
  const blob = new Blob([WORKER_SOURCE], { type: 'application/javascript' });
  cachedWorkerUrl = URL.createObjectURL(blob);
  return cachedWorkerUrl;
}

/**
 * Faz uma request HTTP usando um Worker criado de Blob URL, à prova de
 * intercepção por extensões.
 *
 * @param {Object} opts
 * @param {string} opts.url - URL completa da request
 * @param {string} [opts.method='POST']
 * @param {Object} [opts.headers]
 * @param {string} [opts.body] - corpo string (JSON ou texto)
 * @param {File} [opts.file] - se fornecido, envia como multipart/form-data
 *                              com campo 'file'. Ignora opts.body neste caso.
 * @param {Function} [opts.onProgress] - callback(pct: number)
 * @returns {Promise<{status: number, ok: boolean, data: any}>}
 */
export function extensionSafeRequest({ url, method = 'POST', headers = {}, body, file, onProgress }) {
  return new Promise((resolve, reject) => {
    let worker;
    try {
      worker = new Worker(getWorkerUrl());
    } catch (e) {
      reject(new Error('Worker indisponível: ' + (e?.message || 'unknown')));
      return;
    }

    worker.onmessage = (ev) => {
      const msg = ev.data || {};
      if (msg.type === 'progress') {
        if (typeof onProgress === 'function') {
          try { onProgress(msg.percent); } catch (_) {}
        }
      } else if (msg.type === 'done') {
        try { worker.terminate(); } catch (_) {}
        resolve({ status: msg.status, ok: msg.ok, data: msg.data });
      } else if (msg.type === 'error') {
        try { worker.terminate(); } catch (_) {}
        reject(new Error(msg.message || 'Falha na request'));
      }
    };
    worker.onerror = (err) => {
      try { worker.terminate(); } catch (_) {}
      reject(new Error(err.message || 'Erro no worker'));
    };

    // Monta payload para o worker. Importante: enviamos o File como Blob
    // diretamente — Blobs são structured-cloneable, e o Blob URL Worker
    // recebe sem interferência da extensão.
    let payload;
    if (file) {
      payload = {
        url,
        method,
        headers,
        bodyKind: 'file',
        fileBlob: file,
        fileName: file.name,
      };
    } else {
      payload = {
        url,
        method,
        headers,
        bodyKind: body == null ? 'none' : 'string',
        body: body == null ? null : String(body),
      };
    }

    worker.postMessage(payload);
  });
}

/**
 * Detecta se o erro vem de uma extensão de navegador interceptando requests.
 * Útil para mostrar mensagem específica ao usuário.
 */
export function isExtensionInterferenceError(err) {
  if (!err) return false;
  const msg = (err.message || '').toLowerCase();
  return (
    msg.includes('postmessage') ||
    msg.includes('could not be cloned') ||
    msg.includes('request object') ||
    msg.includes('not cloneable')
  );
}
